import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PreOrderModal from "./PreOrderModal";
import { useAuth } from "@/context/AuthContext";

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { isLoggedIn, isProductSaved, saveProduct, unsaveProduct } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const isSaved = isProductSaved(product.id);

  // Get all images (main image + additional images)
  const getAllImages = () => {
    const images = [];
    if (product.image_url) images.push(product.image_url);
    if (product.images && product.images.length > 0) {
      product.images.forEach(img => {
        if (!images.includes(img)) images.push(img);
      });
    }
    return images.length > 0 ? images : [product.image_url];
  };

  const allImages = getAllImages();
  const hasMultipleImages = allImages.length > 1;

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageLoaded(false);
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageLoaded(false);
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const categoryLabels = {
    "lanterns": "Lantern",
    "candle-holders": "Candle Holder",
    "vases": "Vase",
    "christmas-tree": "Christmas Tree",
  };

  const handleSaveToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isLoggedIn) {
      navigate("/login");
      toast.info("Please login to save products");
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await unsaveProduct(product.id);
        toast.success("Removed from saved");
      } else {
        await saveProduct(product.id);
        toast.success("Added to saved!");
      }
    } catch (error) {
      toast.error("Failed to update saved products");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div 
        className="product-card group relative bg-card rounded-lg overflow-hidden"
        data-testid={`product-card-${product.id}`}
      >
        {/* Image Container */}
        <div className="img-hover-zoom aspect-square relative bg-muted">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          <img
            src={allImages[currentImageIndex]}
            alt={product.name}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Category Badge */}
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 text-[10px] font-medium uppercase tracking-widest bg-background/90 backdrop-blur-sm rounded-sm text-foreground/70">
              {categoryLabels[product.category] || product.category}
            </span>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveToggle}
            disabled={saving}
            className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              isSaved 
                ? "bg-primary text-primary-foreground" 
                : "bg-background/90 text-foreground/70 hover:text-primary"
            }`}
            data-testid={`save-btn-${product.id}`}
          >
            <Heart className={`w-5 h-5 ${isSaved ? "fill-current" : ""}`} />
          </button>

          {/* Image Navigation Arrows - Only show if multiple images */}
          {hasMultipleImages && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-background z-20"
                data-testid={`prev-img-${product.id}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-background z-20"
                data-testid={`next-img-${product.id}`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Image Dots Indicator */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                {allImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (index !== currentImageIndex) {
                        setImageLoaded(false);
                        setCurrentImageIndex(index);
                      }
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      currentImageIndex === index 
                        ? "bg-primary w-4" 
                        : "bg-background/70 hover:bg-background"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Featured Badge */}
          {product.is_featured && (
            <div className="absolute bottom-4 left-4 z-10">
              <span className="badge-preorder px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full text-white">
                Featured
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <Link to={`/product/${product.id}`}>
            <h3 className="font-heading text-base font-medium text-foreground hover:text-primary transition-colors line-clamp-1">
              {product.name}
            </h3>
          </Link>
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
            {product.description}
          </p>
          <div className="flex items-center justify-end mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="text-primary hover:text-primary hover:bg-primary/10 font-medium text-xs tracking-wide"
              data-testid={`preorder-text-btn-${product.id}`}
            >
              Enquire Now
            </Button>
          </div>
        </div>
      </div>

      <PreOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductCard;
